import React, { createContext, useState, useContext, useEffect, useRef, ReactNode } from 'react';
import { Toast, ToastProps } from '../toast';
import { LiveRegion, useAnnounce, UseAnnounceResult } from '../live-region';
export type ToastType = 'success' | 'error' | 'info';

interface ToastContextType {
    toasts: ToastProps[];
    showToast: (toast: ToastProps) => void;
    hideToast: (key?: string | number) => void;
}

const ToastContext = createContext<ToastContextType | undefined>(undefined);

let toastId = 0;
export const ToastProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [toasts, setToasts] = useState<ToastProps[]>([]);
    const polite = useAnnounce();
    const assertive = useAnnounce();
    // rendered toast nodes, keyed by toast key, so non-string content can be read back once mounted
    const toastNodes = useRef(new Map<string | number, HTMLDivElement>());
    const pendingTextAnnouncements = useRef(new Map<string | number, UseAnnounceResult>());

    // runs after every commit: picks up any toast queued for a DOM text read in showToast
    useEffect(() => {
        if (pendingTextAnnouncements.current.size === 0) {
            return;
        }
        pendingTextAnnouncements.current.forEach((announcer, key) => {
            const node = toastNodes.current.get(key);
            // innerText keeps rendered line and block breaks as whitespace; jsdom has no layout, so falls back
            const content = (node?.innerText ?? node?.textContent)?.replace(/\s+/g, ' ').trim();
            if (content) {
                announcer.announce(content);
            }
        });
        pendingTextAnnouncements.current.clear();
    });

    const showToast = (toast: ToastProps) => {
        const newToast = { ...toast, key: toast.key || toastId++, visible: true };
        setToasts((prev) => [...prev, newToast]);

        const announcer = toast.type === 'alert' ? assertive : polite;
        const parts = [toast.message, toast.description].filter(
            (part) => part !== undefined && part !== null && part !== '',
        );

        if (parts.every((part) => typeof part === 'string')) {
            // Always-mounted regions announce reliably; a live region inserted with its text often isn't read.
            const text = parts.join('. ');
            if (text) {
                announcer.announce(text);
            }
        } else if (parts.length > 0) {
            // non-string content (e.g. JSX): read back what actually renders, once it's mounted
            pendingTextAnnouncements.current.set(newToast.key, announcer);
        }

        setTimeout(() => {
            hideToast(newToast.key);
        }, newToast.duration || 3000);
    };

    const hideToast = (key?: string | number) => {
        setToasts((prev) => prev.map((toast) => (toast.key === key ? { ...toast, visible: false } : toast)));
        setTimeout(() => {
            setToasts((prev) => prev.filter((toast) => toast.key !== key));
        }, 300);
    };

    return (
        <ToastContext.Provider value={{ toasts, showToast, hideToast }}>
            {children}
            <LiveRegion politeness="polite" testId="toast-announcer-polite">
                {polite.message}
            </LiveRegion>
            <LiveRegion politeness="assertive" testId="toast-announcer-assertive">
                {assertive.message}
            </LiveRegion>
            <div
                style={{
                    position: 'fixed',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '16px',
                    top: 24,
                    right: 5,
                    zIndex: 9999,
                }}
            >
                {toasts.map((toast) =>
                    toast.visible ? (
                        <Toast
                            ref={(node) => {
                                if (toast.key === undefined) {
                                    return;
                                }
                                if (node) {
                                    toastNodes.current.set(toast.key, node);
                                } else {
                                    toastNodes.current.delete(toast.key);
                                }
                            }}
                            testId={toast.testId}
                            ariaLabel={toast.ariaLabel}
                            key={toast.key}
                            type={toast.type}
                            description={toast.description}
                            message={toast.message}
                            duration={toast.duration}
                            closeLabel={toast.closeLabel}
                            announce={false}
                            onClose={() => hideToast(toast.key)}
                        />
                    ) : null,
                )}
            </div>
        </ToastContext.Provider>
    );
};

export const useToast = () => {
    const context = useContext(ToastContext);
    if (!context) {
        throw new Error('useToast must be used within a ToastProvider');
    }
    return context;
};
